// MessageDialog.tsx
import React from "react";
import { Dialog, DialogContent, DialogActions, Button, Typography, Box } from "@mui/material";

interface MessageDialogProps {
    open: boolean;
    title?: string;
    message?: string;   // optional
    onClose: () => void;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: string;
    cancelColor?: string;
    children?: React.ReactNode; // <-- add this
    icon?: React.ReactNode;
    singleButton?: boolean;
    confirmDisabled?: boolean;
}

export const MessageDialog: React.FC<MessageDialogProps> = ({
    open,
    title,
    message,
    onClose,
    onConfirm,
    confirmText = "OK",
    cancelText = "Cancel",
    confirmColor = "#d32f2f",
    cancelColor = "#e0e0e0",
    icon,
    children,
    singleButton = false,
    confirmDisabled,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 450,        // slightly narrower
                    borderRadius: 2,
                    overflow: "hidden",
                    fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                },
            }}
        >
            {title && (
                <Box
                    sx={{
                        backgroundColor: "#6f6f6f",
                        color: "white",
                        px: 1.5,
                        py: 1,           // reduce vertical padding
                        fontWeight: "bold",
                        fontSize: 15,    // smaller title
                        fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                    }}
                >
                    {title}
                </Box>
            )}

            <DialogContent
                sx={{
                    py: 1.5,          // reduce vertical padding
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                }}
            >
                {icon && <Box>{icon}</Box>}
                {children ?? (
                    <Typography
                        sx={{
                            fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                            fontSize: 14,
                        }}
                    >
                        {message}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 1.5, pb: 1.5, gap: 1 }}>
                {/* Cancel button only if not single-button */}
                {!singleButton && (
                    <Button
                        onClick={onClose}
                        sx={{
                            backgroundColor: cancelColor,
                            color: "#000",
                            fontSize: 13,    // smaller button text
                            padding: "4px 10px",
                            "&:hover": { backgroundColor: "#d5d5d5" },
                            fontFamily: "'Manrope', 'Roboto', 'Segoe UI', sans-serif",
                        }}
                        variant="contained"
                    >
                        {cancelText}
                    </Button>
                )}

                {/* Confirm button */}
                <Button
                    onClick={() => {
                        if (onConfirm) onConfirm();
                        onClose();
                    }}
                    disabled={confirmDisabled}   // <-- disable here
                    sx={{
                        backgroundColor: confirmDisabled ? "#ccc" : confirmColor,
                        color: "#fff",
                        fontSize: 12,
                        padding: "4px 10px",
                        "&:hover": { opacity: confirmDisabled ? 1 : 0.9 },
                    }}
                    variant="contained"
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>

    );
};
